import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { TableKit } from '@tiptap/extension-table';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Columns3,
  Heading,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Rows3,
  SquareCode,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Minimal TipTap editor for `rich_text` blocks (FE-ADR-011; tables FE-ADR-027).
 *
 * The toolbar and the schema are BOTH restricted to the backend sanitizer
 * allowlist (curriculum/blocks.py): p, br, strong, em, u, s, a[href],
 * ul, ol, li, h2, h3, blockquote, code, pre, and tables — table, thead, tbody,
 * tr, th, td with colspan/rowspan (ADR-033). StarterKit v3 bundles Link and
 * Underline; horizontal rule is disabled (`<hr>` is not allowlisted — the
 * divider is its own block type) and headings are capped to levels 2–3.
 * Column resizing stays OFF: resized widths ride on style/colwidth attributes
 * the sanitizer strips, so the control would silently revert on save.
 */
const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    horizontalRule: false,
    link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
  }),
  TableKit.configure({ table: { resizable: false } }),
];

/** Prose styling for the editable area and toolbar-marked content. */
const PROSE_CLASS =
  'min-h-28 max-w-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none ' +
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-base [&_h3]:font-semibold ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground ' +
  '[&_a]:text-primary [&_a]:underline ' +
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 ' +
  '[&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_pre_code]:bg-transparent [&_pre_code]:px-0 ' +
  // Tables — the .tableWrapper div is TipTap's own scroll container.
  '[&_.tableWrapper]:my-2 [&_.tableWrapper]:max-w-full [&_.tableWrapper]:overflow-x-auto ' +
  '[&_table]:border-collapse ' +
  '[&_th]:min-w-12 [&_th]:border [&_th]:bg-muted [&_th]:p-1.5 [&_th]:text-left [&_th]:font-semibold ' +
  '[&_td]:min-w-12 [&_td]:border [&_td]:p-1.5 [&_td]:align-top ' +
  '[&_.selectedCell]:bg-primary/10';

interface RichTextBlockEditorProps {
  /** Initial HTML — the editor owns content after mount (parent remounts via
   *  `key` when a different lesson loads). */
  value: string;
  onChange: (html: string) => void;
}

export function RichTextBlockEditor({ value, onChange }: RichTextBlockEditorProps) {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value || '',
    // Toolbar active-states need re-render on selection/mark changes (v3
    // defaults this off).
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: PROSE_CLASS } },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
  });

  if (!editor) return null;

  return (
    <div className="space-y-1.5">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

const BTN =
  'rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40';
const ACTIVE = 'bg-muted text-foreground';

interface Tool {
  icon: typeof Bold;
  label: string;
  /** Omitted for one-shot actions (not toggles) — no aria-pressed then. */
  isActive?: () => boolean;
  run: () => void;
}

function ToolButton({ tool }: { tool: Tool }) {
  const Icon = tool.icon;
  const active = tool.isActive?.() ?? false;
  return (
    <button
      type="button"
      aria-label={tool.label}
      title={tool.label}
      aria-pressed={tool.isActive ? active : undefined}
      // Keep editor focus/selection — a plain click would blur the editor
      // first, collapsing the selection the command should apply to.
      onMouseDown={e => e.preventDefault()}
      onClick={tool.run}
      className={cn(BTN, active && ACTIVE)}
    >
      <Icon className="size-4" />
    </button>
  );
}

/** Inline +/− button for the table controls (text glyph, not an icon). */
function TableStep({ label, glyph, run }: { label: string; glyph: '+' | '−'; run: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={e => e.preventDefault()}
      onClick={run}
      className={cn(BTN, 'w-7 text-sm font-medium leading-none')}
    >
      {glyph}
    </button>
  );
}

/** Row/column/header/delete controls — rendered while the cursor is inside a
 *  table. A toolbar section rather than dev13-spa's BubbleMenu: no floating-ui
 *  dependency, and the controls stay keyboard-reachable (FE-ADR-027). */
function TableControls({ editor }: { editor: Editor }) {
  const { t } = useTranslation(['curriculum']);
  const chain = () => editor.chain().focus();
  const groupLabel = 'flex items-center gap-1 px-1 text-xs text-muted-foreground';
  return (
    <>
      <span className="mx-0.5 h-5 w-px shrink-0 bg-border" aria-hidden />
      <span className={groupLabel}>
        <Rows3 className="size-3.5" /> {t('curriculum:editor.toolbar.tableRow')}
      </span>
      <TableStep
        glyph="+"
        label={t('curriculum:editor.toolbar.tableAddRow')}
        run={() => chain().addRowAfter().run()}
      />
      <TableStep
        glyph="−"
        label={t('curriculum:editor.toolbar.tableDeleteRow')}
        run={() => chain().deleteRow().run()}
      />
      <span className={groupLabel}>
        <Columns3 className="size-3.5" /> {t('curriculum:editor.toolbar.tableColumn')}
      </span>
      <TableStep
        glyph="+"
        label={t('curriculum:editor.toolbar.tableAddColumn')}
        run={() => chain().addColumnAfter().run()}
      />
      <TableStep
        glyph="−"
        label={t('curriculum:editor.toolbar.tableDeleteColumn')}
        run={() => chain().deleteColumn().run()}
      />
      <ToolButton
        tool={{
          icon: Heading,
          label: t('curriculum:editor.toolbar.tableHeaderRow'),
          run: () => chain().toggleHeaderRow().run(),
        }}
      />
      <ToolButton
        tool={{
          icon: Trash2,
          label: t('curriculum:editor.toolbar.tableDelete'),
          run: () => chain().deleteTable().run(),
        }}
      />
    </>
  );
}

/** Exactly the backend-allowlisted controls — nothing else (FE-ADR-011). */
function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation(['curriculum']);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const tools: Tool[] = [
    {
      icon: Bold,
      label: t('curriculum:editor.toolbar.bold'),
      isActive: () => editor.isActive('bold'),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      icon: Italic,
      label: t('curriculum:editor.toolbar.italic'),
      isActive: () => editor.isActive('italic'),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      icon: Underline,
      label: t('curriculum:editor.toolbar.underline'),
      isActive: () => editor.isActive('underline'),
      run: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      icon: Strikethrough,
      label: t('curriculum:editor.toolbar.strike'),
      isActive: () => editor.isActive('strike'),
      run: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      icon: Heading2,
      label: t('curriculum:editor.toolbar.h2'),
      isActive: () => editor.isActive('heading', { level: 2 }),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      icon: Heading3,
      label: t('curriculum:editor.toolbar.h3'),
      isActive: () => editor.isActive('heading', { level: 3 }),
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      icon: List,
      label: t('curriculum:editor.toolbar.bulletList'),
      isActive: () => editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      icon: ListOrdered,
      label: t('curriculum:editor.toolbar.orderedList'),
      isActive: () => editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      icon: Quote,
      label: t('curriculum:editor.toolbar.blockquote'),
      isActive: () => editor.isActive('blockquote'),
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      icon: SquareCode,
      label: t('curriculum:editor.toolbar.codeBlock'),
      isActive: () => editor.isActive('codeBlock'),
      run: () => editor.chain().focus().toggleCodeBlock().run(),
    },
    {
      icon: TableIcon,
      label: t('curriculum:editor.toolbar.table'),
      isActive: () => editor.isActive('table'),
      run: () => {
        // No nested tables — inside one, the row/column controls take over.
        if (!editor.isActive('table')) {
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
        }
      },
    },
  ];

  function applyLink() {
    // The sanitizer only keeps http/https hrefs — normalize bare domains.
    const raw = linkUrl.trim();
    const href = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
    const chain = editor.chain().focus().extendMarkRange('link');
    if (href) chain.setLink({ href }).run();
    else chain.unsetLink().run();
    setLinkOpen(false);
    setLinkUrl('');
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-0.5 rounded-md border p-1" role="toolbar">
        {tools.map(tool => (
          <ToolButton key={tool.label} tool={tool} />
        ))}
        <ToolButton
          tool={{
            icon: Link2,
            label: t('curriculum:editor.toolbar.link'),
            isActive: () => editor.isActive('link'),
            run: () => {
              setLinkUrl((editor.getAttributes('link').href as string) ?? '');
              setLinkOpen(open => !open);
            },
          }}
        />
        {editor.isActive('table') ? <TableControls editor={editor} /> : null}
      </div>
      {linkOpen ? (
        <div className="flex items-center gap-2">
          <Input
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
            }}
            aria-label={t('curriculum:editor.toolbar.linkUrl')}
            placeholder="https://…"
            className="h-8"
          />
          <Button type="button" size="sm" onMouseDown={e => e.preventDefault()} onClick={applyLink}>
            {t('curriculum:editor.toolbar.linkApply')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
